import { useEffect, useRef } from 'react';
import backend from '../backend';

const REFRESH_DEBOUNCE_MS = 1200;
const FALLBACK_POLLING_MS = 60000;

type ConnectedEvent = {
  clientId?: number;
  adventureId?: string;
  at?: number;
};

export const useAdventureSyncGlue = (
  adventureId: string,
  refreshAll: () => void | Promise<void>,
  onRefreshFailed?: () => void
) => {
  const refreshTimeoutRef = useRef<number | null>(null);
  const fallbackIntervalRef = useRef<number | null>(null);

  useEffect(() => {
    const safelyRefresh = () => {
      Promise.resolve()
        .then(() => refreshAll())
        .catch((error) => {
          console.error('Adventure sync refresh failed:', error);
          onRefreshFailed?.();
        });
    };

    const scheduleRefresh = () => {
      if (refreshTimeoutRef.current !== null) {
        window.clearTimeout(refreshTimeoutRef.current);
      }
      refreshTimeoutRef.current = window.setTimeout(() => {
        safelyRefresh();
      }, REFRESH_DEBOUNCE_MS);
    };

    const startFallbackPolling = () => {
      if (fallbackIntervalRef.current !== null) {
        return;
      }
      fallbackIntervalRef.current = window.setInterval(() => {
        scheduleRefresh();
      }, FALLBACK_POLLING_MS);
    };

    const stopFallbackPolling = () => {
      if (fallbackIntervalRef.current === null) {
        return;
      }
      window.clearInterval(fallbackIntervalRef.current);
      fallbackIntervalRef.current = null;
    };

    const eventSource = new EventSource(backend.getAdventureEventsUrl(adventureId));
    eventSource.addEventListener('connected', (event) => {
      try {
        const data = JSON.parse((event as MessageEvent).data || '{}') as ConnectedEvent;
        backend.setSseSubscriberId(typeof data.clientId === 'number' ? data.clientId : undefined);
      } catch (_e) {
        backend.setSseSubscriberId(undefined);
      }
      stopFallbackPolling();
    });
    eventSource.addEventListener('adventure.changed', () => {
      scheduleRefresh();
    });
    eventSource.onerror = () => {
      backend.setSseSubscriberId(undefined);
      startFallbackPolling();
    };

    return () => {
      eventSource.close();
      backend.setSseSubscriberId(undefined);
      stopFallbackPolling();
      if (refreshTimeoutRef.current !== null) {
        window.clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
    };
  }, [adventureId, refreshAll, onRefreshFailed]);
};